import { useRef, useEffect, useState } from "react";
import { toISODate, isOverdue } from "@/lib/taskConstants";
import { EVENT_TYPE_MAP } from "@/components/calendar/EventDialog";

const HOUR_H = 52;
const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const toMin = (hhmm) => {
  const [h, m] = (hhmm || "00:00").split(":").map(Number);
  return h * 60 + (m || 0);
};
const fmtMin = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const addDaysIso = (iso, n) => {
  const [y, mo, d] = iso.split("-").map(Number);
  const dt = new Date(y, mo - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

const TASK_STATE_ICON = (t) => {
  if (t.status === "done") return "✅";
  if (isOverdue(t)) return "🔴";
  if (t.status === "in_progress") return "🔵";
  if (t.status === "review") return "🟣";
  return "⚪";
};

const assignLanes = (items) => {
  const lanes = [];
  for (const it of items) {
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      if (lanes[i] <= it.startMin) {
        it.lane = i;
        lanes[i] = it.endMin;
        placed = true;
        break;
      }
    }
    if (!placed) {
      it.lane = lanes.length;
      lanes.push(it.endMin);
    }
  }
  return lanes.length || 1;
};

const buildDayData = (days, events, tasks) =>
  days.map((d) => {
    const iso = toISODate(d);
    const allDayEvents = events
      .filter((e) => iso >= e.start_date && iso <= e.end_date && (e.all_day || !e.start_time || e.start_date !== e.end_date))
      .sort((a, b) => a.title.localeCompare(b.title));
    const timed = events
      .filter((e) => !e.all_day && e.start_time && e.start_date === iso && e.start_date === e.end_date)
      .map((e) => {
        const startMin = toMin(e.start_time);
        const endMin = e.end_time ? Math.max(toMin(e.end_time), startMin + 30) : startMin + 60;
        return { ...e, startMin, endMin };
      })
      .sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);
    const laneCount = assignLanes(timed);
    const dayTasks = tasks.filter((t) => {
      const s = t.start_date || t.due_date;
      const e2 = t.due_date || t.start_date;
      return iso >= s && iso <= e2;
    });
    return { iso, date: d, allDayEvents, timed, laneCount, dayTasks };
  });

const useEventDrag = ({ days, onEvent, onEventMove }) => {
  const [drag, setDrag] = useState(null);

  const startEventDrag = (e, ev, colEl) => {
    e.stopPropagation();
    const canDrag = ev.can_edit && !ev.is_occurrence && onEventMove;
    const colW = colEl.getBoundingClientRect().width;
    const sx = e.clientX, sy = e.clientY;
    let moved = false;
    const onMove = (me) => {
      if (!canDrag) return;
      const dx = me.clientX - sx, dy = me.clientY - sy;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) moved = true;
      setDrag({
        id: ev.id,
        dyPx: Math.round(dy / (HOUR_H / 2)) * (HOUR_H / 2),
        dxPx: days.length > 1 ? Math.round(dx / colW) * colW : 0,
      });
    };
    const onUp = (me) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setDrag(null);
      const dx = me.clientX - sx, dy = me.clientY - sy;
      if (!moved || (Math.abs(dx) < 5 && Math.abs(dy) < 5)) {
        onEvent(ev);
        return;
      }
      const deltaMin = Math.round((dy / HOUR_H) * 60 / 30) * 30;
      const deltaDays = days.length > 1 ? Math.round(dx / colW) : 0;
      if (!deltaMin && !deltaDays) return;
      const dur = ev.endMin - ev.startMin;
      const newStart = Math.min(Math.max(0, ev.startMin + deltaMin), 24 * 60 - dur);
      const newIso = addDaysIso(ev.start_date, deltaDays);
      onEventMove(ev, {
        start_date: newIso, end_date: newIso,
        start_time: fmtMin(newStart), end_time: fmtMin(newStart + dur),
      });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return { drag, startEventDrag };
};

const AllDayCell = ({ iso, allDayEvents, dayTasks, colorMap, onEvent, onTask }) => (
  <div className="border-l p-1 space-y-0.5 min-h-[30px]" data-testid={`allday-cell-${iso}`}>
    {allDayEvents.slice(0, 3).map((ev) => (
      <button key={`${ev.id}-${ev.start_date}`} onClick={() => onEvent(ev)} data-testid={`calendar-event-${ev.id}`}
        className="w-full text-left text-[10px] leading-tight px-1.5 py-0.5 rounded truncate block text-white font-medium hover:opacity-80"
        style={{ backgroundColor: ev.color || colorMap[ev.source] }}>
        {EVENT_TYPE_MAP[ev.event_type]?.icon} {ev.title}
      </button>
    ))}
    {dayTasks.slice(0, 3).map((t) => (
      <button key={t.id} onClick={() => onTask(t)} data-testid={`calendar-gtask-${t.id}`}
        title={t.assignee_name ? `Responsable: ${t.assignee_name}` : "Sin responsable"}
        className="w-full text-left text-[10px] leading-tight px-1.5 py-0.5 rounded truncate flex items-center gap-1 border hover:opacity-80"
        style={t.assignee_color
          ? { backgroundColor: `${t.assignee_color}1f`, borderColor: `${t.assignee_color}80` }
          : undefined}>
        <span className="text-[8px] leading-none shrink-0">{TASK_STATE_ICON(t)}</span>
        <span className={`truncate ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</span>
      </button>
    ))}
    {allDayEvents.length + dayTasks.length > 6 && (
      <span className="text-[10px] text-muted-foreground px-1">+{allDayEvents.length + dayTasks.length - 6} más</span>
    )}
  </div>
);

const TimedEvent = ({ ev, laneCount, colorMap, drag, startEventDrag }) => (
  <button key={`${ev.id}-${ev.start_date}`} data-testid={`calendar-event-${ev.id}`}
    onPointerDown={(e) => startEventDrag(e, ev, e.currentTarget.parentElement)}
    className={`absolute rounded-md px-1.5 py-0.5 text-left text-white overflow-hidden hover:opacity-90 shadow-sm select-none ${
      drag?.id === ev.id ? "z-30 opacity-80 shadow-lg" : "z-10"}`}
    style={{
      top: (ev.startMin / 60) * HOUR_H + 1,
      height: Math.max(24, ((ev.endMin - ev.startMin) / 60) * HOUR_H - 2),
      left: `calc(${(ev.lane * 100) / laneCount}% + 2px)`,
      width: `calc(${100 / laneCount}% - 5px)`,
      backgroundColor: ev.color || colorMap[ev.source],
      cursor: ev.can_edit && !ev.is_occurrence ? "grab" : "pointer",
      transform: drag?.id === ev.id ? `translate(${drag.dxPx}px, ${drag.dyPx}px)` : undefined,
    }}>
    <span className="block text-[10px] font-semibold leading-tight truncate">
      {EVENT_TYPE_MAP[ev.event_type]?.icon} {ev.title}
    </span>
    <span className="block text-[9px] opacity-85 leading-tight">
      {ev.start_time}{ev.end_time ? `–${ev.end_time}` : ""}
    </span>
  </button>
);

const DayColumn = ({ iso, timed, laneCount, today, nowMin, onSlot, colorMap, drag, startEventDrag }) => (
  <div className="relative border-l" data-testid={`timegrid-day-${iso}`}>
    {Array.from({ length: 24 }, (_, h) => (
      <div key={h} onClick={() => onSlot(iso, `${String(h).padStart(2, "0")}:00`)}
        data-testid={`timegrid-slot-${iso}-${h}`}
        className="absolute inset-x-0 border-t border-border/50 hover:bg-secondary/40 cursor-pointer transition-colors"
        style={{ top: h * HOUR_H, height: HOUR_H }} />
    ))}
    {iso === today && (
      <div className="absolute inset-x-0 z-20 pointer-events-none" style={{ top: (nowMin / 60) * HOUR_H }} data-testid="now-line">
        <div className="h-0.5 bg-red-500 relative">
          <span className="absolute -left-1 -top-[3px] h-2 w-2 rounded-full bg-red-500" />
        </div>
      </div>
    )}
    {timed.map((ev) => (
      <TimedEvent key={`${ev.id}-${ev.start_date}`} ev={ev} laneCount={laneCount}
        colorMap={colorMap} drag={drag} startEventDrag={startEventDrag} />
    ))}
  </div>
);

export const TimeGrid = ({ days, events, tasks, colorMap, today, onEvent, onTask, onSlot, onEventMove }) => {
  const scrollRef = useRef(null);
  const { drag, startEventDrag } = useEventDrag({ days, onEvent, onEventMove });
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 7.5 * HOUR_H });
  }, []);

  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const dayData = buildDayData(days, events, tasks);
  const cols = days.length;

  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="time-grid">
      {/* Cabecera días */}
      <div className="grid border-b bg-secondary/40" style={{ gridTemplateColumns: `56px repeat(${cols}, 1fr)` }}>
        <div />
        {dayData.map(({ iso, date }) => (
          <div key={iso} className="py-2 text-center text-xs font-semibold text-muted-foreground border-l">
            {WEEKDAYS[(date.getDay() + 6) % 7]}{" "}
            <span className={`inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full ${iso === today ? "bg-primary text-primary-foreground font-bold" : ""}`}>
              {date.getDate()}
            </span>
          </div>
        ))}
      </div>
      {/* Franja todo el día + tareas */}
      <div className="grid border-b" style={{ gridTemplateColumns: `56px repeat(${cols}, 1fr)` }}>
        <div className="text-[10px] text-muted-foreground px-1 py-1.5 text-right">Día</div>
        {dayData.map(({ iso, allDayEvents, dayTasks }) => (
          <AllDayCell key={iso} iso={iso} allDayEvents={allDayEvents} dayTasks={dayTasks}
            colorMap={colorMap} onEvent={onEvent} onTask={onTask} />
        ))}
      </div>
      {/* Rejilla horaria */}
      <div ref={scrollRef} className="overflow-y-auto" style={{ maxHeight: 560 }}>
        <div className="grid relative" style={{ gridTemplateColumns: `56px repeat(${cols}, 1fr)`, height: 24 * HOUR_H }}>
          {/* Columna de horas */}
          <div className="relative">
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} className="absolute right-1.5 text-[10px] text-muted-foreground -translate-y-1/2"
                style={{ top: h * HOUR_H }}>
                {h > 0 ? `${String(h).padStart(2, "0")}:00` : ""}
              </div>
            ))}
          </div>
          {/* Columnas de días */}
          {dayData.map(({ iso, timed, laneCount }) => (
            <DayColumn key={iso} iso={iso} timed={timed} laneCount={laneCount} today={today}
              nowMin={nowMin} onSlot={onSlot} colorMap={colorMap} drag={drag} startEventDrag={startEventDrag} />
          ))}
        </div>
      </div>
    </div>
  );
};
